import {
  Component, OnDestroy, ChangeDetectionStrategy, computed, effect, inject, output, input, signal, untracked, viewChild
} from '@angular/core';
import {Subscription} from 'rxjs';
import moment from 'moment-timezone';
import {ImpressionService} from '@app/services/impression.service';
import {
  SbTableColumn,
  SbTableOptions,
  SbTableRowBase,
  SbTableServerState
} from '@app/_components/unified/sb-table/sb-table.types';
import { InfoPopoverComponent } from '../../../_components/info-popover/info-popover.component';
import { MatMenuTrigger, MatMenu } from '@angular/material/menu';
import { SbTableComponent } from '../../../_components/unified/sb-table/sb-table.component';
import { SbTableCellDirective, SbTableChildDirective } from '../../../_components/unified/sb-table/sb-table.directives';
import { DecimalPipe } from '@angular/common';
import { NgbPopover } from '@ng-bootstrap/ng-bootstrap';
import { humanDuration, timeToRegisterText } from '@app/_helpers/time-to-register';

export interface ConversionsSummary {
  conversions: number;
  referrers: number;
  averagePerReferrer: number;
  unmatchedReferrers: number;
}

export interface ConversionsReferrerRow extends SbTableRowBase {
  id: number;
  matched: boolean;
  name: string;
  email: string;
  type: string;
  jobTitle: string;
  company: string;
  conversions: number;
}

/** How a conversion's time was found: a tracked click (on the site or off it) or a match on older data. */
export type ConversionsMatchSource = 'token' | 'token_offsite' | 'fingerprint' | 'ip' | 'code';

export type JourneyStageKey = 'linkToSeen' | 'seenToShare' | 'shareToClick' | 'clickToConversion';

/** How fine the bins are: the server picks it from the report's date range. */
export type JourneyTier = 'day' | 'week' | 'month' | 'long';

/** A time range, `[fromSeconds, toSeconds)`; the last bin is open-ended. */
export interface JourneyBin {
  key: string;
  fromSeconds: number;
  toSeconds: number | null;
}

/** One step of the referral journey: how many started it in the range, how many got through it and how fast. */
export interface JourneyStage {
  key: JourneyStageKey;
  unit: string;
  started: number;
  reached: number;
  notReached: number;
  /** One per bin, in the bins' order; sums to `reached`. */
  counts: number[];
  averageSeconds: number | null;
  medianSeconds: number | null;
  /** The key of the bin with the most items; null when none reached the step. */
  mostCommon: string | null;
  /** Items timed from page visits, before widget interactions were tracked. */
  approximate: number;
  /** clickToConversion only: per bin, how the conversions' clicks were found. */
  bySource?: Partial<Record<ConversionsMatchSource, number>>[];
  noClick?: number;
  beforeRelease?: number;
  /** clickToConversion only: reached through a fingerprint, IP or the referrer's clicks alone. */
  lessAccurate?: number;
}

/** The referral journey, as the server answers `sections=journey`. */
export interface JourneyReport {
  tier: JourneyTier;
  bins: JourneyBin[];
  stages: JourneyStage[];
}

/** A bin's column in a stage's row. */
export interface JourneyBarView {
  key: string;
  label: string;
  count: number;
  share: string;
  /** Height against the row's tallest column, in percent. */
  height: number;
  mostCommon: boolean;
  ariaLabel: string;
  /** clickToConversion only: the bin's conversions by how their click was found, the empty sources left out. */
  sources: {label: string; count: number}[];
}

export interface JourneyRowView {
  stage: JourneyStage;
  name: string;
  from: string;
  to: string;
  unit: string;
  bars: JourneyBarView[];
  median: string;
  average: string;
}

const MATCH_SOURCE_LABELS: Record<ConversionsMatchSource, string> = {
  token: 'Tracked click',
  token_offsite: 'Tracked click, off-site',
  fingerprint: 'Device fingerprint',
  ip: 'IP address',
  code: 'Referrer\'s clicks'
};

/** Each stage's name, where it starts and ends, and what it counts. */
const JOURNEY_STAGES: Record<JourneyStageKey, {name: string; from: string; to: string; unit: string}> = {
  linkToSeen: {
    name: 'Link received → widget seen', from: 'launch email sent', to: 'first use of the share widget', unit: 'advocates'
  },
  seenToShare: {
    name: 'Widget seen → first share', from: 'share widget first used, or registration', to: 'first share', unit: 'advocates'
  },
  shareToClick: {
    name: 'Shared → audience clicked', from: 'first share', to: 'first visit of the shared link', unit: 'advocates who shared'
  },
  clickToConversion: {
    name: 'Clicked → converted', from: 'call-to-action click', to: 'registration', unit: 'conversions'
  }
};

/** A bin boundary as a number and the largest unit it is a whole number of: 90 s, 5 m, 12 h, 3 d. */
const boundary = (seconds: number): {value: number; unit: string} => {
  for (const [size, unit] of [[86400, 'd'], [3600, 'h'], [60, 'm']] as [number, string][]) {
    if (seconds >= size && seconds % size === 0) {
      return {value: seconds / size, unit};
    }
  }
  return {value: seconds, unit: 's'};
};

/**
 * A bin's axis label, from its bounds: "<1 m", "1–5 m", "15 m–1 h", ">3 d". In two parts, so a
 * narrow column breaks the label after the dash; a number keeps its unit (no-break space).
 */
export const journeyBinLabel = (bin: JourneyBin): [string, string] => {
  const text = ({value, unit}: {value: number; unit: string}) => `${value} ${unit}`;
  if (bin.toSeconds == null) {
    return [`>${text(boundary(bin.fromSeconds))}`, ''];
  }
  const to = boundary(bin.toSeconds);
  if (!bin.fromSeconds) {
    return [`<${text(to)}`, ''];
  }
  const from = boundary(bin.fromSeconds);
  return from.unit === to.unit ? [`${from.value}–`, text(to)] : [`${text(from)}–`, text(to)];
};

/** A count's share of the reached, as the columns label it: whole percents, "<1%" for a few. */
const shareLabel = (count: number, reached: number): string => {
  const share = reached ? (count / reached) * 100 : 0;
  if (count > 0 && share < 0.5) {
    return '<1%';
  }
  return `${Math.round(share)}%`;
};

/** A referrer's people, as far as their opened row has loaded them. */
export interface ConversionsReferred {
  list?: any[];
  total?: number;
  page?: number;
  loading?: boolean;
}

/**
 * The project report's Conversions tab: who referred whom, under the report's own filters.
 * The parent owns the filters and the export's progress; this asks the server for the figures
 * and the referrers, and each referrer's people when their row is opened.
 */
@Component({
  selector: 'app-report-conversions',
  templateUrl: './report-conversions.component.html',
  // The report page's cards, so the tab reads as part of it.
  styleUrls: ['../report/report.component.scss', './report-conversions.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    InfoPopoverComponent, MatMenuTrigger, MatMenu, SbTableComponent, SbTableCellDirective, SbTableChildDirective, DecimalPipe,
    NgbPopover
  ]
})
export class ReportConversionsComponent implements OnDestroy {
  private impressionService = inject(ImpressionService);

  readonly projectId = input<number>(undefined);
  readonly campaignId = input<number>(null);
  readonly mode = input('live');
  readonly segmentIds = input<number[]>(null);
  readonly influencerTypeIds = input<number[]>(null);
  readonly startDate = input<any>(null);
  readonly endDate = input<any>(null);
  readonly canExport = input(false);
  /** Staff only: say when the times include matches less accurate than a tracked click. */
  readonly showMatchAccuracy = input(false);
  /** Only conversions that took less than this many seconds from the click; null for all. */
  readonly timeToRegisterLt = input<number | null>(null);
  readonly exportRequest = output<{
    exportType: string;
    params: any;
  }>();

  readonly summary = signal<ConversionsSummary>(null);
  readonly summaryLoading = signal(false);
  readonly journey = signal<JourneyReport>(null);
  readonly rows = signal<ConversionsReferrerRow[]>([]);
  readonly totalRows = signal(0);
  readonly error = signal('');
  /**
   * Each opened referrer's people, by referrer id. The table renders a child row outside this
   * view, so it reads them from a signal rather than from fields set on the row.
   */
  readonly referred = signal<Record<number, ConversionsReferred>>({});

  readonly referredPerPage = 50;

  /** The shared axis under the rows: each bin's label, in two parts that may break after the dash. */
  readonly journeyAxis = computed(() => (this.journey()?.bins || []).map(journeyBinLabel));

  /** One row per stage; null when no stage had anyone start it. */
  readonly journeyRows = computed<JourneyRowView[]>(() => {
    const journey = this.journey();
    if (!journey?.stages?.some((stage) => stage.started > 0)) {
      return null;
    }
    const labels = journey.bins.map((bin) => journeyBinLabel(bin).join(''));
    return journey.stages.map((stage) => {
      const meta = JOURNEY_STAGES[stage.key] || {name: stage.key, from: '', to: '', unit: stage.unit};
      const counts = journey.bins.map((_bin, index) => stage.counts?.[index] || 0);
      const tallest = Math.max(...counts, 0);
      return {
        stage,
        name: meta.name,
        from: meta.from,
        to: meta.to,
        unit: meta.unit,
        median: humanDuration(stage.medianSeconds),
        average: humanDuration(stage.averageSeconds),
        bars: journey.bins.map((bin, index): JourneyBarView => {
          const count = counts[index];
          const share = shareLabel(count, stage.reached);
          const bySource = stage.bySource?.[index] || {};
          return {
            key: bin.key,
            label: labels[index],
            count,
            share,
            height: tallest ? (count / tallest) * 100 : 0,
            mostCommon: !!stage.reached && bin.key === stage.mostCommon,
            ariaLabel: `${meta.name}, ${labels[index]}: ${count} ${meta.unit}, ${share} of reached`,
            sources: (Object.keys(MATCH_SOURCE_LABELS) as ConversionsMatchSource[])
              .filter((source) => bySource[source] > 0)
              .map((source) => ({label: MATCH_SOURCE_LABELS[source], count: bySource[source]}))
          };
        })
      };
    });
  });

  /** Some stages were timed from page visits, before widget interactions were tracked. */
  readonly journeyApproximate = computed(() => !!this.journey()?.stages?.some((stage) => stage.approximate > 0));

  /** The time-to-register filter, as the card's hint names it: "5 min", "1 day"; null when not set. */
  readonly timeToRegisterLimit = computed(() => {
    const seconds = this.timeToRegisterLt();
    return seconds ? timeToRegisterText(seconds) : null;
  });

  /** What the card describes: the report's mode and campaign filter. */
  readonly journeyScope = computed(() => `${this.mode() === 'test' ? 'Test' : 'Live'} mode, `
    + `${this.campaignId() > 0 ? 'the selected campaign' : 'all campaigns'}.`);

  tableColumns: SbTableColumn[] = [
    {id: 'name', header: 'Referrer'},
    {id: 'email', header: 'Email', field: 'email'},
    {id: 'type', header: 'Type', field: 'type'},
    {id: 'jobTitle', header: 'Job Title', field: 'jobTitle'},
    {id: 'company', header: 'Company', field: 'company'},
    {id: 'conversions', header: 'Conversions', className: 'text-end'}
  ];

  tableOptions: SbTableOptions = {
    serverSide: true,
    selectable: false,
    expandable: true,
    pageLength: 10,
    lengthMenu: [10, 25, 50, 100],
    searchLabel: 'Search referrers',
    emptyMessage: 'No conversions for the selected filters.',
    order: [[6, 'desc']],
  };

  private tableState: SbTableServerState = {page: 1, pageLength: 10, search: '', sort: null};
  private readonly table = viewChild(SbTableComponent);
  private filtersKey = '';
  private summarySub: Subscription;
  private referrersSub: Subscription;

  constructor() {
    // New filters load the report again; an input that leaves them as they were does not.
    effect(() => {
      const key = JSON.stringify(this.filterParams());
      if (!this.projectId() || key === this.filtersKey) {
        return;
      }
      const first = !this.filtersKey;
      this.filtersKey = key;
      untracked(() => {
        this.loadSummary();
        // The table asks for its first page itself once it has drawn. New filters go back to it
        // through the table, so its pager follows and a failure shows in it.
        if (!first) {
          const table = this.table();
          if (table) {
            table.reload(true);
          } else {
            this.tableState = {...this.tableState, page: 1};
            this.loadReferrers();
          }
        }
      });
    });
  }

  ngOnDestroy(): void {
    this.summarySub?.unsubscribe();
    this.referrersSub?.unsubscribe();
  }

  /** The report's filters as the server reads them, for the tab and for its export. */
  filterParams(): any {
    const campaignId = this.campaignId();
    const influencerTypeIds = this.influencerTypeIds();
    const segmentIds = this.segmentIds();
    const startDate = this.startDate();
    const endDate = this.endDate();
    const mode = this.mode();
    const timeToRegisterLt = this.timeToRegisterLt();
    return {
      ...(campaignId && campaignId > 0 ? {campaignId} : {}),
      ...(influencerTypeIds && influencerTypeIds.length ? {influencerTypeId: JSON.stringify(influencerTypeIds)} : {}),
      ...(segmentIds && segmentIds.length ? {segmentId: JSON.stringify(segmentIds)} : {}),
      ...(startDate ? {startDate: moment(startDate).format('YYYY-MM-DD')} : {}),
      ...(endDate ? {endDate: moment(endDate).format('YYYY-MM-DD')} : {}),
      ...(mode ? {mode} : {}),
      ...(timeToRegisterLt ? {timeToRegisterLt} : {})
    };
  }

  onTableState(state: SbTableServerState): void {
    this.tableState = state;
    this.loadReferrers();
  }

  onRowExpand(event: {row: SbTableRowBase; expanded: boolean}): void {
    const row = event.row as ConversionsReferrerRow;
    if (event.expanded && !this.referredOf(row).list) {
      this.loadReferred(row, 1);
    }
  }

  /** What has loaded of a referrer's people. */
  referredOf(row: ConversionsReferrerRow): ConversionsReferred {
    return this.referred()[row.id] || {};
  }

  loadReferred(row: ConversionsReferrerRow, page: number): void {
    this.setReferred(row, {loading: true});
    this.impressionService.reportConversions(this.projectId(), {
      ...this.filterParams(),
      sections: 'referred',
      referrerId: row.id,
      page,
      perPage: this.referredPerPage
    }).subscribe({
      next: (resp: any) => {
        const referred = resp?.data?.referred;
        this.setReferred(row, {
          list: [...(page > 1 ? this.referredOf(row).list || [] : []), ...(referred?.list || [])],
          total: referred?.paging?.total || 0,
          page,
          loading: false
        });
      },
      error: () => {
        this.setReferred(row, {list: this.referredOf(row).list || [], loading: false});
      }
    });
  }

  private setReferred(row: ConversionsReferrerRow, changes: ConversionsReferred): void {
    this.referred.update((all) => ({...all, [row.id]: {...all[row.id], ...changes}}));
  }

  exportData(exportType: string): void {
    this.exportRequest.emit({exportType, params: this.filterParams()});
  }

  asReferrer(row: SbTableRowBase): ConversionsReferrerRow {
    return row as ConversionsReferrerRow;
  }

  formatDate(date: any): string {
    return date ? moment(date).format('DD-MM-YYYY HH:mm') : '';
  }

  /** The staff notice: how many conversions were timed less accurately than by a tracked click. */
  lessAccurateNotice(): string {
    const count = this.journey()?.stages?.find((stage) => stage.key === 'clickToConversion')?.lessAccurate || 0;
    return `Includes ${count} ${count === 1 ? 'conversion' : 'conversions'} timed by IP, fingerprint or the referrer's clicks alone `
      + '(less accurate than a tracked click).';
  }

  private loadSummary(): void {
    this.summarySub?.unsubscribe();
    this.summaryLoading.set(true);
    this.summarySub = this.impressionService.reportConversions(this.projectId(), {
      ...this.filterParams(),
      sections: 'summary,journey'
    }).subscribe({
      next: (resp: any) => {
        this.summary.set(resp?.data?.summary || null);
        this.journey.set(resp?.data?.journey || null);
        this.summaryLoading.set(false);
        this.error.set('');
      },
      error: (err) => {
        this.summaryLoading.set(false);
        this.error.set(err?.error?.message || 'The conversions could not be loaded.');
      }
    });
  }

  private loadReferrers(): void {
    const projectId = this.projectId();
    if (!projectId) {
      return;
    }
    this.referrersSub?.unsubscribe();
    const sort = this.tableState.sort;
    this.referrersSub = this.impressionService.reportConversions(projectId, {
      ...this.filterParams(),
      sections: 'referrers',
      page: this.tableState.page,
      perPage: this.tableState.pageLength,
      search: this.tableState.search || '',
      ...(sort ? {sort: sort.columnId, dir: sort.direction} : {})
    }).subscribe({
      next: (resp: any) => {
        const referrers = resp?.data?.referrers;
        // New rows: the people loaded for the last page's rows went with them.
        this.referred.set({});
        this.rows.set((referrers?.list || []) as ConversionsReferrerRow[]);
        this.totalRows.set(referrers?.paging?.total || 0);
      },
      error: () => {
        this.referred.set({});
        // The table says the page could not be loaded, with a way to ask again.
        this.rows.set([]);
        this.totalRows.set(0);
      }
    });
  }
}
